import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, IconButton, Popover, Stack } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateRangeCalendar } from "@mui/x-date-pickers-pro/DateRangeCalendar";
import { format, isValid, parseISO } from "date-fns";
import { SovosBox, SovosTypography } from "mosaic-react";
import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";
import {
    ENABLED_REPORTING_DATE_MODULES,
    buildSearchWithoutReportingDate,
    buildSearchWithReportingDate,
    createReportingDateStorageKey,
    getStoredReportingDateRange,
    hasReportingDateAvailableFilter,
    hasReportingDateFilter,
    isReportingDateHeaderEnabled,
    parseReportingDateRangeFromFilters,
    setStoredReportingDateRange,
} from "./utils";

const emptyRange = { from: null, to: null };
const emptyCalendarRange = [null, null];

const parseDateValue = (value) => {
    if (!value) {
        return null;
    }
    const parsedValue = parseISO(value);
    return isValid(parsedValue) ? parsedValue : null;
};

const formatDateValue = (value) => {
    if (!value) {
        return "";
    }
    return format(value, "yyyy-MM-dd");
};

const parseFiscalYearReferenceDate = (fiscalYear) => {
    if (!fiscalYear) {
        return null;
    }
    const yearNumber = Number(fiscalYear);
    if (!Number.isInteger(yearNumber) || yearNumber < 1000 || yearNumber > 9999) {
        return null;
    }
    return new Date(yearNumber, 0, 1);
};

const ReportingDateHeader = ({
    allowWithoutAvailableFilters = false,
    availableFilters,
    forceModulePrefix,
    projectModules,
    width = "280px",
}) => {
    const { history, i18n, location, modules, params } = useGlobalValue();
    const search = location?.search || history?.location?.search || "";
    const pathname = location?.pathname || history?.location?.pathname || "";
    const effectiveModules = Array.isArray(projectModules) && projectModules.length ? projectModules : modules;
    const effectiveModulePrefix =
        forceModulePrefix ||
        params?.modulePrefix ||
        ENABLED_REPORTING_DATE_MODULES.find((moduleName) => effectiveModules?.includes(moduleName));
    const storageKey = useMemo(
        () => createReportingDateStorageKey({ projectId: params?.projectId }),
        [params?.projectId],
    );
    const isFeatureEnabled = isReportingDateHeaderEnabled({
        modulePrefix: effectiveModulePrefix,
        modules: effectiveModules,
    });
    const areAvailableFiltersLoaded = Array.isArray(availableFilters);
    const hasAvailableFilter = hasReportingDateAvailableFilter(availableFilters);
    const isEnabled = allowWithoutAvailableFilters
        ? isFeatureEnabled
        : isFeatureEnabled && hasAvailableFilter;
    const shouldStripFilter = allowWithoutAvailableFilters
        ? !isFeatureEnabled
        : !isFeatureEnabled || (areAvailableFiltersLoaded && !hasAvailableFilter);
    const [range, setRange] = useState([emptyRange]);
    const lastHydratedPathRef = useRef(null);
    const [calendarRange, setCalendarRange] = useState(emptyCalendarRange);
    const [anchorEl, setAnchorEl] = useState(null);
    const fiscalYearReferenceDate = useMemo(
        () => parseFiscalYearReferenceDate(params?.fiscalYear),
        [params?.fiscalYear],
    );

    const syncRange = (nextRange) => {
        const normalizedRange = {
            from: nextRange?.from || null,
            to: nextRange?.to || null,
        };
        setRange([normalizedRange]);
        setCalendarRange([
            parseDateValue(normalizedRange.from),
            parseDateValue(normalizedRange.to),
        ]);
    };

    const applyRange = (nextRange) => {
        const normalizedRange = {
            from: nextRange?.from || null,
            to: nextRange?.to || null,
        };
        const nextSearch = buildSearchWithReportingDate(search, normalizedRange);
        syncRange(normalizedRange);
        setStoredReportingDateRange(storageKey, normalizedRange);
        history?.replace?.({
            pathname,
            search: nextSearch ? `?${nextSearch}` : "",
        });
        setAnchorEl(null);
    };

    useEffect(() => {
        const filter = new URLSearchParams(search).get("filter");
        const hasFilterInUrl = hasReportingDateFilter(filter);

        if (!isEnabled) {
            if (shouldStripFilter && hasFilterInUrl) {
                const nextSearch = buildSearchWithoutReportingDate(search);
                history?.replace?.({
                    pathname,
                    search: nextSearch ? `?${nextSearch}` : "",
                });
            }

            if (shouldStripFilter) {
                syncRange(emptyRange);
            }
            return;
        }

        const rangeFromUrl = parseReportingDateRangeFromFilters(filter);

        if (rangeFromUrl.from || rangeFromUrl.to) {
            syncRange(rangeFromUrl);
            setStoredReportingDateRange(storageKey, rangeFromUrl);
            lastHydratedPathRef.current = pathname;
            return;
        }

        const isNewPath = lastHydratedPathRef.current !== pathname;

        if (!isNewPath) {
            syncRange(emptyRange);
            return;
        }

        lastHydratedPathRef.current = pathname;
        const storedRange = getStoredReportingDateRange(storageKey);

        if (!storedRange?.from && !storedRange?.to) {
            syncRange(emptyRange);
            return;
        }

        syncRange(storedRange);

        const nextSearch = buildSearchWithReportingDate(search, storedRange);
        history?.replace?.({
            pathname,
            search: nextSearch ? `?${nextSearch}` : "",
        });
    }, [history, isEnabled, pathname, search, shouldStripFilter, storageKey]);

    if (!isEnabled) {
        return null;
    }

    const currentRange = range[0] || emptyRange;
    const popoverOpen = Boolean(anchorEl);

    return (
        <SovosBox sx={{ minWidth: width, width }}>
            <SovosBox
                onClick={(event) => setAnchorEl(event.currentTarget)}
                sx={{
                    alignItems: "center",
                    cursor: "pointer",
                    display: "flex",
                    gap: 1,
                    minHeight: 40,
                    width: "100%",
                }}
            >
                <IconButton size="small">
                    <CalendarMonthIcon fontSize="small" />
                </IconButton>
                <Stack direction="row" spacing={2} sx={{ alignItems: "flex-end", flex: 1, minWidth: 0 }}>
                    <SovosBox sx={{ minWidth: 140 }}>
                        <SovosTypography sx={{ color: "text.secondary", fontSize: "0.75rem", lineHeight: 1.2 }}>
                            {i18n`From`}
                        </SovosTypography>
                        <SovosTypography sx={{ fontSize: "0.875rem", lineHeight: 1.6 }}>
                            {currentRange.from || ""}
                        </SovosTypography>
                    </SovosBox>
                    <SovosBox sx={{ minWidth: 140 }}>
                        <SovosTypography sx={{ color: "text.secondary", fontSize: "0.75rem", lineHeight: 1.2 }}>
                            {i18n`To`}
                        </SovosTypography>
                        <SovosTypography sx={{ fontSize: "0.875rem", lineHeight: 1.6 }}>
                            {currentRange.to || ""}
                        </SovosTypography>
                    </SovosBox>
                </Stack>
            </SovosBox>
            <Popover
                anchorEl={anchorEl}
                open={popoverOpen}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
                <SovosBox sx={{ p: 2 }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DateRangeCalendar
                            value={calendarRange}
                            onChange={(nextValue) => {
                                setCalendarRange(nextValue || emptyCalendarRange);
                            }}
                            calendars={2}
                            {...(fiscalYearReferenceDate ? { referenceDate: fiscalYearReferenceDate } : {})}
                        />
                    </LocalizationProvider>
                    <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end", pt: 1 }}>
                        <Button
                            onClick={() => applyRange(emptyRange)}
                            size="small"
                        >
                            {i18n`Reset`}
                        </Button>
                        <Button
                            onClick={() => {
                                applyRange({
                                    from: formatDateValue(calendarRange?.[0]),
                                    to: formatDateValue(calendarRange?.[1]),
                                });
                            }}
                            size="small"
                            variant="contained"
                        >
                            {i18n`Apply`}
                        </Button>
                    </Stack>
                </SovosBox>
            </Popover>
        </SovosBox>
    );
};

export default ReportingDateHeader;
